import React, { useState } from 'react';
import { Copy, Check, Fingerprint } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

interface RegistrationCodeBadgeProps {
  code: string;
  className?: string;
  showCopyLabel?: boolean;
}

export function RegistrationCodeBadge({
  code,
  className,
  showCopyLabel = false
}: RegistrationCodeBadgeProps) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setError(null);

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Falha ao copiar código');
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setError(null);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        setError('Não foi possível copiar o código');
      }
    }
  };

  const formatRegistrationCode = (code: string) => {
    // Format code as XXX-XXX if it's 6 digits
    if (code.length === 6) {
      return code.slice(0, 3) + '-' + code.slice(3);
    }
    return code;
  };

  return (
    <div className={cn(
      'registration-code-badge',
      'inline-flex items-center gap-2',
      'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20',
      'border border-blue-200 dark:border-blue-800',
      'rounded-lg px-3 py-2',
      'transition-all duration-200',
      'group hover:shadow-sm',
      className
    )}>
      <Fingerprint
        className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
        aria-hidden="true"
      />
      <span
        className="font-mono font-semibold text-blue-900 dark:text-blue-100"
        aria-label={`Código de registro: ${formatRegistrationCode(code)}`}
      >
        {formatRegistrationCode(code)}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className={cn(
          'h-6 w-6 p-0 ml-1',
          'text-muted-foreground hover:text-primary dark:text-muted-foreground dark:hover:text-primary',
          'transition-colors duration-200',
          copied && 'text-finance-green dark:text-finance-green'
        )}
        aria-label={copied ? 'Código copiado!' : 'Copiar código'}
        title={copied ? 'Código copiado!' : 'Copiar código'}
      >
        {copied ? (
          <Check className="h-3 w-3" aria-hidden="true" />
        ) : (
          <Copy className="h-3 w-3" aria-hidden="true" />
        )}
      </Button>
      {showCopyLabel && (
        <span className="text-xs text-muted-foreground hidden sm:inline">
          {copied ? 'Copiado!' : 'Copiar'}
        </span>
      )}
      {error && (
        <span
          className="text-xs text-red-600 dark:text-red-400 absolute -bottom-5 left-0"
          role="alert"
        >
          {error}
        </span>
      )}
    </div>
  );
}